import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, AlertTriangle, Ban, Scale } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Navigation } from '@/components/Navigation';
import { useMemo } from 'react';

// Generate a random colorful gradient
function generateRandomGradient(): string[] {
  const gradients = [
    ['#667eea', '#764ba2'],
    ['#f093fb', '#f5576c'],
    ['#4facfe', '#00f2fe'],
    ['#43e97b', '#38f9d7'],
    ['#fa709a', '#fee140'],
    ['#30cfd0', '#330867'],
    ['#a8edea', '#fed6e3'],
    ['#ff9a56', '#ff6a88'],
    ['#ffecd2', '#fcb69f'],
    ['#ff6e7f', '#bfe9ff'],
    ['#e0c3fc', '#8ec5fc'],
    ['#f8b500', '#fceabb'],
    ['#fdcbf1', '#e6dee9'],
    ['#a1c4fd', '#c2e9fb'],
    ['#d299c2', '#fef9d7'],
    ['#89f7fe', '#66a6ff'],
    ['#fddb92', '#d1fdff'],
    ['#9890e3', '#b1f4cf'],
    ['#ebc0fd', '#d9ded8'],
    ['#96fbc4', '#f9f586'],
  ];

  return gradients[Math.floor(Math.random() * gradients.length)];
}

export function TermsOfServicePage() {
  // Generate random gradient on component mount (page refresh)
  const gradient = useMemo(() => generateRandomGradient(), []);

  const backgroundStyle = {
    background: `linear-gradient(135deg, ${gradient.join(', ')})`,
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Blurred gradient background */}
      <div
        className="fixed top-0 left-0 right-0 h-[50vh] bg-cover bg-center blur-3xl opacity-25 pointer-events-none"
        style={backgroundStyle}
      />

      {/* Global Navigation */}
      <Navigation />

      {/* Terms Content */}
      <div className="relative container mx-auto px-4 py-8 max-w-3xl">
        <div className="mb-6">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </Link>
        </div>

        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
          <p className="text-muted-foreground">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        <div className="space-y-6">
          {/* Introduction */}
          <Card className="glass border-white/10">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-primary/10 flex-shrink-0">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div className="space-y-3">
                  <h2 className="text-xl font-semibold">Welcome to NodeCast</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    By using NodeCast, you agree to these terms. Please read them carefully.
                    NodeCast is a decentralized music sharing platform built on the Nostr protocol.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Third-Party Hosting */}
          <Card className="glass border-white/10">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-sky-500/10 flex-shrink-0">
                  <Shield className="h-6 w-6 text-sky-500" />
                </div>
                <div className="space-y-3">
                  <h2 className="text-xl font-semibold">Third-Party Hosting</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    <strong className="text-foreground">NodeCast does not host music files.</strong> All audio files are stored on third-party Blossom servers,
                    not on NodeCast's infrastructure. Currently, NodeCast uses the{' '}
                    <a
                      href="https://primal.net"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      primal.net
                    </a>
                    {' '}Blossom server, but may utilize other Blossom servers in the future.
                  </p>
                  <p className="text-sm text-muted-foreground italic">
                    Blossom servers are decentralized file storage providers that operate independently from NodeCast.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Copyright Compliance */}
          <Card className="glass border-white/10">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-purple-500/10 flex-shrink-0">
                  <Scale className="h-6 w-6 text-purple-500" />
                </div>
                <div className="space-y-3">
                  <h2 className="text-xl font-semibold">Copyright & Ownership</h2>
                  <div className="space-y-2 text-muted-foreground leading-relaxed">
                    <p>
                      <strong className="text-foreground">You must own the rights to content you upload.</strong> Do not submit copyrighted
                      material unless you hold the copyright or have explicit permission from the copyright holder.
                    </p>
                    <p>
                      NodeCast reserves the right to filter out copyrighted content and content that violates these terms.
                      Violations may result in blacklisting from the platform.
                    </p>
                    <div className="mt-4 p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                      <p className="text-sm">
                        <strong className="text-purple-400">For Copyright Holders:</strong> If you believe your copyrighted
                        work has been uploaded without authorization, please contact the Blossom server provider directly.
                        For content hosted on primal.net, reach out to their support team. NodeCast facilitates metadata only
                        and does not store the actual files.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Anti-Spam Policy */}
          <Card className="glass border-white/10">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-orange-500/10 flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-orange-500" />
                </div>
                <div className="space-y-3">
                  <h2 className="text-xl font-semibold">Anti-Spam Policy</h2>
                  <div className="space-y-2 text-muted-foreground leading-relaxed">
                    <p>
                      <strong className="text-foreground">Spamming is strictly prohibited.</strong> Users who engage in
                      spam activities will be blacklisted from the platform.
                    </p>
                    <p>
                      Consistent abuse of the platform, including repeated spam violations, will result in IP-based blacklisting
                      to protect the community and maintain platform integrity.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content Policy */}
          <Card className="glass border-white/10">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-red-500/10 flex-shrink-0">
                  <Ban className="h-6 w-6 text-red-500" />
                </div>
                <div className="space-y-3">
                  <h2 className="text-xl font-semibold">Content Policy</h2>
                  <div className="space-y-2 text-muted-foreground leading-relaxed">
                    <p>
                      <strong className="text-foreground">Illegal content is strictly prohibited.</strong> Do not upload,
                      share, or distribute any content that violates local, national, or international laws.
                    </p>
                    <p>
                      <strong className="text-foreground">NodeCast is a music platform.</strong> All published events must be
                      music-related. Non-music content will be filtered out, and users who repeatedly publish non-music
                      events will be blacklisted.
                    </p>
                    <div className="mt-4 p-4 bg-red-500/5 border border-red-500/20 rounded-lg">
                      <p className="text-sm">
                        <strong className="text-red-400">Zero Tolerance:</strong> Illegal content, including but not limited to
                        illegal pornography, content promoting violence, hate speech, or content violating intellectual property
                        rights, will result in immediate and permanent blacklisting.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Enforcement */}
          <Card className="glass border-white/10">
            <CardContent className="p-6">
              <div className="space-y-3">
                <h2 className="text-xl font-semibold">Enforcement & Moderation</h2>
                <p className="text-muted-foreground leading-relaxed">
                  NodeCast employs automated and manual moderation to enforce these terms. We reserve the right to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>Filter or remove content that violates these terms</li>
                  <li>Blacklist users who repeatedly violate these terms</li>
                  <li>Implement IP-based restrictions for consistent abusers</li>
                  <li>Update these terms at any time to protect the community</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card className="glass border-white/10">
            <CardContent className="p-6">
              <div className="space-y-3">
                <h2 className="text-xl font-semibold">Questions?</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have questions about these terms, please reach out via Nostr or through our community channels.
                  By continuing to use NodeCast, you acknowledge that you have read, understood, and agree to these Terms of Service.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom spacing */}
        <div className="h-12" />
      </div>
    </div>
  );
}
